import React, { useState } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import colors from '../../../../themes/main-colors';
import ReactMarkdown from 'react-markdown/with-html';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PGEButton from '../../../buttons';
import { OutageAcknowledgementCard } from '../outage-acknowledgement-card/OutageAcknowledgementCard';
import GreenCheckIcon from '../../../../static/greenCheckIcon.svg';
import { OutageReportSummary } from '../outage-report-summary-dialog/OutageReportSummary';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    background: colors.white,
    padding: '1rem 1rem',
  },
  box: {
    margin: '0rem 1rem',
    [theme.breakpoints.down('sm')]: {
      margin: '1rem',
    },
  },
  accordionRoot: {
    marginTop: '1rem',
  },
  accordion: {
    marginBottom: '1rem',
    boxShadow: 'none',
    '&.MuiAccordion-root.Mui-expanded': {
      marginBottom: '1rem',
    },
  },
  accordionSummary: {
    fontSize: 16,
    fontWeight: 600,
  },
  imageStyles: {
    display: 'flex',
    justifyContent: 'center',
  },
  parahStyles: {
    fontSize: 16,
  },
  subButtonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '2rem',
    gap: '15px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  reportOutageMsg: {
    width: '50%',
    borderRadius: '25px',
    fontSize: '16px',
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  alertsButton: {
    color: colors.sparkBlue,
    background: colors.white,
    borderColor: colors.sparkBlue,
    borderRadius: theme.spacing(3.75),
    minWidth: '100px',
    borderWidth: '2px',
    padding: '8px',
    width: '50%',
    '& span': {
      fontSize: '16px',
      fontWeight: 600,
    },
    '&:hover': {
      background: colors.white,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
}));

export const ResettingBreakersDialog = () => {
  const { t, richT } = useTranslation();
  const classes = useStyles();
  const [type, setType] = useState<string | null>(null);
  const { setReportOutageOpen } = useOutageLeftPanelState();

  const renderAccordionComp = (
    parah?: React.ReactNode,
    image?: React.ReactNode,
  ) => {
    return (
      <>
        <div css={{ width: '100%' }}>
          <Divider />
          {parah && <div className={classes.parahStyles}>{parah}</div>}
          {image && <div className={classes.imageStyles}>{image}</div>}
        </div>
      </>
    );
  };

  const renderAccordionData = [
    {
      header: t('HOW_TO_RESET_YOUR_BREAKER'),
      parah: (
        <ReactMarkdown
          source={t('RESET_BREAKERS_ACCORDION1')}
          escapeHtml={false}
        />
      ),
      image: (
        <img
          alt=""
          src={require('../../../../images/InsideBreaker.png')}
          width={230}
          height={200}
        />
      ),
    },
    {
      header: t('WHERE_CAN_I_FIND_INSIDE_BREAKER'),
      parah: (
        <ReactMarkdown
          source={t('RESET_BREAKERS_ACCORDION2')}
          escapeHtml={false}
        />
      ),
      image: '',
    },
    {
      header: t('HOW_TO_FIND_BREAKER_IN_COMMERCIAL_BUILDING'),
      parah: (
        <ReactMarkdown
          source={t('RESET_BREAKERS_ACCORDION3')}
          escapeHtml={false}
        />
      ),
      image: (
        <img
          alt=""
          src={require('../../../../images/OutsideBreaker.png')}
          width={230}
          height={250}
        />
      ),
    },
  ];

  return (
    <>
      {!type && (
        <Box className={classes.box}>
          <div className={classes.content}>
            <Typography>{richT('RESETTING_BREAKERS_CONTENT')}</Typography>
          </div>
          <div className={classes.accordionRoot}>
            {renderAccordionData?.map((faq, idx) => (
              <div key={idx}>
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div className={classes.accordionSummary}>{faq.header}</div>
                  </AccordionSummary>
                  <AccordionDetails>
                    {renderAccordionComp(faq.parah, faq.image)}
                  </AccordionDetails>
                </Accordion>
              </div>
            ))}
          </div>
          <Grid className={classes.subButtonContainer}>
            <Button
              variant={'outlined'}
              className={classes.alertsButton}
              onClick={() => setType('didNotWork')}
            >
              {t('THIS_DID_NOT_WORK_REPORT_OUTAGE')}
            </Button>
            <PGEButton
              onClick={() => setType('ResettingBreakerWorked')}
              size={'large'}
              variant={'contained'}
              className={classes.reportOutageMsg}
            >
              {t('RESETTING_THE_BREAKER_WORKED')}
            </PGEButton>
          </Grid>
        </Box>
      )}
      {type === 'didNotWork' && <OutageReportSummary />}
      {type === 'ResettingBreakerWorked' && (
        <OutageAcknowledgementCard
          icon={<GreenCheckIcon />}
          cardHeader={t('GREAT_NEWS')}
          content={richT('GREAT_NEWS_CONTENT')}
          buttonOne={'Done'}
          buttonOneOnClick={() => {
            setReportOutageOpen(false);
          }}
        />
      )}
    </>
  );
};
